/**
 * 功能开关注册表（P-21）。
 *
 * 这是「能授予什么能力」的全局边界登记表：每个产品维护一组开关键
 * （key / 类型 / 说明 / 默认值），套餐的 `entitlements` JSONB 只存值，
 * 这里存键的元信息，后台才能渲染权益表单、校验键名、回答「哪些套餐开了功能 X」。
 *
 * 契约：openapi §EntitlementKey —— GET 列表带 product scope（按产品拉取），
 * POST 注册 / PATCH 修改 / DELETE 删除（全局 by id）。列表返回**纯数组**（非分页）。
 * 真实后端端点尚未实现，前端 + mock 已就绪，待后端补齐即可联调。
 */
import { useMemo, useState, type ReactNode } from 'react';
import { useAsync } from '../lib/useAsync';
import { formatDate } from '../lib/format';
import { Icon } from '../icons/Icon';
import { ICON_SIZE } from '../tokens';
import { ErrorBlock, CardSkeleton } from '../components/DataState';
import {
  listProducts,
  listEntitlementKeys,
  createEntitlementKey,
  updateEntitlementKey,
  deleteEntitlementKey,
} from '../api/endpoints';
import type {
  EntitlementKey,
  EntitlementValue,
  EntitlementValueType,
  CreateEntitlementKeyRequest,
  UpdateEntitlementKeyRequest,
  Product,
} from '../api/types';

const VALUE_TYPE_LABEL: Record<EntitlementValueType, string> = {
  boolean: '布尔',
  integer: '整数',
  string: '字符串',
};

const KEY_PATTERN = /^[a-z0-9][a-z0-9_]{1,62}$/;

type DialogState =
  | null
  | { mode: 'create' }
  | { mode: 'edit'; key: EntitlementKey }
  | { mode: 'delete'; key: EntitlementKey };

type Feedback = { tone: 'ok' | 'warn' | 'error'; text: string };

export function EntitlementsPage() {
  const products = useAsync(() => listProducts(), []);
  const [productId, setProductId] = useState<string>('');

  const keys = useAsync(
    () => listEntitlementKeys(productId || undefined),
    [productId],
  );

  const [dialog, setDialog] = useState<DialogState>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const productName = useMemo(() => {
    if (products.state.kind !== 'ready' || !productId) return null;
    return products.state.data.items.find((p) => p.id === productId)?.name ?? null;
  }, [products.state, productId]);

  const productNameOf = (id: string) =>
    products.state.kind === 'ready'
      ? products.state.data.items.find((p) => p.id === id)?.name ?? id.slice(0, 8)
      : id.slice(0, 8);

  const closeDialog = () => setDialog(null);
  const clearFeedbackSoon = () => window.setTimeout(() => setFeedback(null), 6000);

  const handleCreated = () => {
    setFeedback({ tone: 'ok', text: '已注册功能开关。它将作为该产品能力边界的一部分生效。' });
    closeDialog();
    keys.reload();
    clearFeedbackSoon();
  };
  const handleUpdated = () => {
    setFeedback({ tone: 'ok', text: '已保存修改。' });
    closeDialog();
    keys.reload();
    clearFeedbackSoon();
  };
  const handleDeleted = (k: EntitlementKey) => {
    setFeedback({ tone: 'warn', text: `已删除功能开关「${k.display_name}」（${k.key}）。` });
    closeDialog();
    keys.reload();
    clearFeedbackSoon();
  };
  const handleError = (e: unknown) => {
    setFeedback({ tone: 'error', text: e instanceof Error ? e.message : '操作失败' });
    clearFeedbackSoon();
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">功能开关注册表</h1>
          <p className="text-muted" style={{ marginTop: 4 }}>
            定义「能授予什么能力」的全局边界；开关本身不含业务值，只定义能力边界。
          </p>
        </div>
        <div className="page-head__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setDialog({ mode: 'create' })}
          >
            <Icon name="plus" size={ICON_SIZE.control} />
            新增开关
          </button>
        </div>
      </div>

      <div className="field" style={{ maxWidth: 360 }}>
        <label>产品</label>
        <select className="select" value={productId} onChange={(e) => setProductId(e.target.value)}>
          <option value="">全部产品</option>
          {products.state.kind === 'ready' &&
            products.state.data.items.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </select>
      </div>

      {feedback && (
        <div className={`notice notice--${feedback.tone}`}>
          <span className="notice__icon">
            <Icon
              name={feedback.tone === 'error' ? 'octagon-x' : feedback.tone === 'warn' ? 'circle-alert' : 'circle-check'}
              size={ICON_SIZE.inline}
            />
          </span>
          <span>{feedback.text}</span>
        </div>
      )}

      {keys.state.kind === 'loading' && (
        <div className="card">
          <CardSkeleton lines={4} />
        </div>
      )}

      {keys.state.kind === 'failed' && (
        <ErrorBlock message="功能开关注册表加载失败" onRetry={() => keys.reload()} />
      )}

      {keys.state.kind === 'ready' && keys.state.data.length === 0 && (
        <div className="empty-state">
          <Icon name="sliders-horizontal" size={ICON_SIZE.standalone} />
          <p>{productId ? `「${productName ?? '该产品'}」尚未注册任何功能开关。` : '尚未注册任何功能开关。'}</p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setDialog({ mode: 'create' })}
          >
            <Icon name="plus" size={ICON_SIZE.control} />
            新增第一个开关
          </button>
        </div>
      )}

      {keys.state.kind === 'ready' && keys.state.data.length > 0 && (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>开关标识</th>
                <th>产品</th>
                <th>说明</th>
                <th>类型</th>
                <th>默认值</th>
                <th>创建时间</th>
                <th className="table__actions-col">操作</th>
              </tr>
            </thead>
            <tbody>
              {keys.state.data.map((k) => (
                <tr key={k.id}>
                  <td>
                    <code className="code-cell">{k.key}</code>
                  </td>
                  <td>{productNameOf(k.product_id)}</td>
                  <td>{k.display_name}</td>
                  <td>
                    <span className={`ent-type-badge ent-type-badge--${k.value_type}`}>
                      {VALUE_TYPE_LABEL[k.value_type]}
                    </span>
                  </td>
                  <td>{renderDefault(k.default_value)}</td>
                  <td className="text-muted">{formatDate(k.created_at)}</td>
                  <td className="table__actions-col">
                    <button
                      type="button"
                      className="btn btn--quiet btn--icon"
                      onClick={() => setDialog({ mode: 'edit', key: k })}
                      aria-label="编辑"
                    >
                      <Icon name="pencil" size={ICON_SIZE.control} />
                    </button>
                    <button
                      type="button"
                      className="btn btn--quiet btn--icon"
                      onClick={() => setDialog({ mode: 'delete', key: k })}
                      aria-label="删除"
                    >
                      <Icon name="trash" size={ICON_SIZE.control} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog?.mode === 'create' && (
        <EntitlementKeyDialog
          mode="create"
          initialProductId={productId || undefined}
          products={products.state.kind === 'ready' ? products.state.data.items : []}
          onClose={closeDialog}
          onCreated={handleCreated}
          onError={handleError}
        />
      )}
      {dialog?.mode === 'edit' && (
        <EntitlementKeyDialog
          mode="edit"
          products={[]}
          keyDef={dialog.key}
          onClose={closeDialog}
          onUpdated={handleUpdated}
          onError={handleError}
        />
      )}
      {dialog?.mode === 'delete' && (
        <DeleteKeyDialog
          keyDef={dialog.key}
          onClose={closeDialog}
          onDeleted={() => handleDeleted(dialog.key)}
          onError={handleError}
        />
      )}
    </div>
  );
}

function renderDefault(value: EntitlementKey['default_value']): ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-muted">未设置</span>;
  }
  if (typeof value === 'boolean') {
    return (
      <span className={`chip chip--${value ? 'ok' : 'off'}`}>
        <Icon name={value ? 'circle-check' : 'circle-dot'} size={ICON_SIZE.inline} />
        {value ? '是' : '否'}
      </span>
    );
  }
  return <code className="code-cell">{String(value)}</code>;
}

/* ────────────────────────── 对话框 ────────────────────────── */

function DialogShell({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  return (
    <div className="panel-overlay" onClick={onClose}>
      <div
        className="panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="panel__head">
          <h2 className="panel__title">{title}</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          {children}
          {footer}
        </div>
      </div>
    </div>
  );
}

function defaultValueToStr(value: EntitlementValue | null | undefined): string {
  return value == null ? '' : String(value);
}

function parseDefault(type: EntitlementValueType, raw: string): EntitlementValue | null {
  if (type === 'boolean') return raw === 'true';
  if (type === 'integer') {
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }
  return raw;
}

function DefaultValueInput({
  valueType,
  value,
  onChange,
}: {
  valueType: EntitlementValueType;
  value: string;
  onChange: (v: string) => void;
}) {
  if (valueType === 'boolean') {
    return (
      <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="true">是</option>
        <option value="false">否</option>
      </select>
    );
  }
  if (valueType === 'integer') {
    return <input className="input" type="number" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0" />;
  }
  return <input className="input" type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder="默认值" />;
}

function EntitlementKeyDialog({
  mode,
  initialProductId,
  products,
  keyDef,
  onClose,
  onCreated,
  onUpdated,
  onError,
}: {
  mode: 'create' | 'edit';
  initialProductId?: string;
  products: readonly Product[];
  keyDef?: EntitlementKey;
  onClose: () => void;
  onCreated?: () => void;
  onUpdated?: () => void;
  onError: (e: unknown) => void;
}) {
  const isEdit = mode === 'edit';
  const [selectedProductId, setSelectedProductId] = useState(initialProductId ?? '');
  const [key, setKey] = useState(keyDef?.key ?? '');
  const [valueType, setValueType] = useState<EntitlementValueType>(keyDef?.value_type ?? 'boolean');
  const [displayName, setDisplayName] = useState(keyDef?.display_name ?? '');
  const [defaultValue, setDefaultValue] = useState<string>(defaultValueToStr(keyDef?.default_value));
  const [defaultNull, setDefaultNull] = useState(keyDef?.default_value == null);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const keyValid = isEdit || KEY_PATTERN.test(key.trim());
  const defaultFilled = defaultNull || defaultValue.trim().length > 0 || valueType === 'boolean';
  const formValid = displayName.trim().length > 0 && keyValid && defaultFilled;
  // T2：新增开关需 type-to-confirm 输入 key（大小写敏感）。编辑不改 key，无需确认。
  const confirmOk = isEdit || confirmText === key.trim();

  const handle = async () => {
    if (submitting || !formValid || !confirmOk) return;
    if (!isEdit && !selectedProductId) return;
    setError(null);
    setSubmitting(true);
    try {
      const parsedDefault: EntitlementValue | null = defaultNull ? null : parseDefault(valueType, defaultValue);
      if (isEdit && keyDef) {
        const body: UpdateEntitlementKeyRequest = {
          display_name: displayName.trim(),
          default_value: parsedDefault,
        };
        await updateEntitlementKey(keyDef.id, body);
        onUpdated?.();
      } else {
        const body: CreateEntitlementKeyRequest = {
          key: key.trim(),
          value_type: valueType,
          display_name: displayName.trim(),
          default_value: parsedDefault,
        };
        await createEntitlementKey(selectedProductId, body);
        onCreated?.();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : '操作失败');
      setSubmitting(false);
      onError(e);
    }
  };

  return (
    <DialogShell
      title={isEdit ? '编辑功能开关' : '注册功能开关'}
      onClose={onClose}
      footer={
        <div className="dialog-actions">
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            取消
          </button>
          <button type="button" className="btn btn--primary" disabled={submitting || !formValid || !confirmOk || (!isEdit && !selectedProductId)} onClick={handle}>
            {submitting ? '提交中…' : isEdit ? '保存' : '注册'}
          </button>
        </div>
      }
    >
      {!isEdit && (
        <div className="t2-band">
          <Icon name="octagon-x" size={ICON_SIZE.inline} />
          <span>
            新增开关走 T2 确认：它将作为该产品能力边界的一部分，可能影响已生效授权。请键入开关标识以确认。
          </span>
        </div>
      )}

      {!isEdit && (
        <div className="field">
          <label>产品</label>
          <select value={selectedProductId} onChange={(e) => setSelectedProductId(e.target.value)}>
            <option value="">选择产品…</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="field">
        <label>开关标识 (key)</label>
        <input
          className="input"
          type="text"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          disabled={isEdit}
          placeholder="concurrent_pools"
          maxLength={64}
        />
        <p className="text-muted" style={{ fontSize: 12 }}>
          小写字母/数字/下划线，如 concurrent_pools。创建后不可改名。
        </p>
      </div>

      <div className="field">
        <label>说明</label>
        <input
          className="input"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="并发 Token 池数量"
          maxLength={120}
        />
      </div>

      <div className="field">
        <label>类型</label>
        <select className="select" value={valueType} onChange={(e) => setValueType(e.target.value as EntitlementValueType)} disabled={isEdit}>
          <option value="boolean">布尔</option>
          <option value="integer">整数</option>
          <option value="string">字符串</option>
        </select>
      </div>

      <div className="field">
        <label className="license-toggle license-toggle--block">
          <input type="checkbox" checked={!defaultNull} onChange={(e) => setDefaultNull(!e.target.checked)} />
          设置默认值
        </label>
        {!defaultNull && <DefaultValueInput valueType={valueType} value={defaultValue} onChange={setDefaultValue} />}
      </div>

      {!isEdit && (
        <div className="field">
          <label>键入开关标识以确认新增</label>
          <input
            className={`input ${confirmText.length > 0 && !confirmOk ? 'input--warn' : ''}`}
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={key.trim()}
          />
          <p className="text-muted" style={{ fontSize: 12 }}>需与上方开关标识完全一致（大小写敏感）。</p>
        </div>
      )}

      {error && (
        <p className="login-error">
          <Icon name="octagon-x" size={ICON_SIZE.inline} />
          {error}
        </p>
      )}
    </DialogShell>
  );
}

function DeleteKeyDialog({
  keyDef,
  onClose,
  onDeleted,
  onError,
}: {
  keyDef: EntitlementKey;
  onClose: () => void;
  onDeleted: () => void;
  onError: (e: unknown) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await deleteEntitlementKey(keyDef.id);
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : '删除失败');
      setSubmitting(false);
      onError(e);
    }
  };

  return (
    <DialogShell
      title="删除功能开关"
      onClose={onClose}
      footer={
        <div className="dialog-actions">
          <button type="button" className="btn btn--quiet" onClick={onClose}>
            取消
          </button>
          <button type="button" className="btn btn--danger" disabled={submitting} onClick={handle}>
            {submitting ? '删除中…' : '删除'}
          </button>
        </div>
      }
    >
      <p>
        确认删除功能开关 <code className="code-cell">{keyDef.key}</code>（{keyDef.display_name}）？
      </p>
      <p className="text-muted" style={{ fontSize: 13 }}>
        若该开关仍被任一套餐引用，删除将被拒绝（服务端返回 9005 has_dependents）。
      </p>
      {error && (
        <p className="login-error">
          <Icon name="octagon-x" size={ICON_SIZE.inline} />
          {error}
        </p>
      )}
    </DialogShell>
  );
}
